import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Step, CallBackProps, STATUS, ACTIONS } from 'react-joyride';
import { Box, Button, Text, VStack, HStack, IconButton, Icon } from 'src/components/ui';
import { CloseIcon } from '@chakra-ui/icons';
import { FaRocket } from 'react-icons/fa';
import ProductTour from 'src/components/common/ProductTour';
import { useOnboarding } from 'src/contexts/onboarding';
import {
  RIGHT_SIDEBAR_WIDTH_COLLAPSED,
  TOP_BAR_HEIGHT,
} from 'src/components/campaigns/utils/constants';

/**
 * Menu Tour Steps
 *
 * These steps target menu items in the left sidebar.
 * Each menu item should have an id attribute matching the target.
 */
const MENU_TOUR_STEPS: Step[] = [
  {
    target: '#menu-home',
    title: 'Home Dashboard',
    content: 'Your central hub. View your campaigns, track performance, and access quick actions all in one place.',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 4,
  },
  {
    target: '#menu-campaigns',
    title: 'Ad Campaigns',
    content: 'Create and manage your advertising campaigns. Our AI helps you design effective ads across multiple platforms.',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 4,
  },
  {
    target: '#menu-voice-agent',
    title: 'AI Voice Agent',
    content: 'Set up an AI-powered voice agent to handle customer calls, answer questions, and qualify leads automatically.',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 4,
  },
  {
    target: '#menu-tasks',
    title: 'Quick Tools',
    content: 'Access AI-powered tools for specific tasks like generating ad copy, images, or analyzing competitors.',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 4,
  },
  {
    target: '#menu-account',
    title: 'Account Settings',
    content: 'Configure your business profile, manage team members, and update your company information.',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 4,
  },
  {
    target: '#menu-commerce',
    title: 'Products & Services',
    content: 'Manage your product catalog. Add the items you want to advertise and keep your inventory organized.',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 4,
  },
  {
    target: '#menu-integrations',
    title: 'Integrations',
    content: 'Connect your Meta and Google accounts to publish ads directly to these platforms.',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 4,
  },
];

/**
 * MenuTour Component
 *
 * A guided tour of the main navigation menu using react-joyride.
 * Shows 6 steps explaining each major section of the app.
 * After completion, triggers a guided cursor to help users create their first campaign.
 */
const MenuTour: React.FC = () => {
  const navigate = useNavigate();
  const {
    showMenuTour,
    closeMenuTour,
    setWizard1Completed,
    triggerGuidedCursor,
  } = useOnboarding();

  const [tourState, setTourState] = React.useState({
    run: false,
    steps: MENU_TOUR_STEPS,
  });

  // State for confirmation modal
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Start tour when showMenuTour becomes true
  useEffect(() => {
    if (showMenuTour) {
      // Navigate to home first so sidebar is visible
      navigate('/home');

      // Small delay to ensure navigation completes and sidebar is rendered
      setTimeout(() => {
        setTourState((prev) => ({ ...prev, run: true }));
      }, 500);
    } else {
      setTourState((prev) => ({ ...prev, run: false }));
    }
  }, [showMenuTour, navigate]);

  const handleTourCallback = useCallback((data: CallBackProps) => {
    const { status, action } = data;

    // Handle tour completion - show confirmation modal
    if (status === STATUS.FINISHED) {
      closeMenuTour();
      // Show confirmation modal instead of directly triggering cursor
      setTimeout(() => {
        setShowConfirmation(true);
      }, 300);
    }

    // Handle skip
    if (status === STATUS.SKIPPED) {
      setWizard1Completed();
      closeMenuTour();
    }

    // Handle close button click
    if (action === ACTIONS.CLOSE) {
      setWizard1Completed();
      closeMenuTour();
    }
  }, [setWizard1Completed, closeMenuTour]);

  // Handle confirmation - user clicks "Yes" to create campaign
  const handleConfirmYes = useCallback(() => {
    setShowConfirmation(false);
    // Trigger guided cursor to guide user to click "Ad Campaigns" then "Create Ads"
    setTimeout(() => {
      triggerGuidedCursor();
    }, 300);
  }, [triggerGuidedCursor]);

  // Handle confirmation - user clicks "Skip" or closes modal
  const handleConfirmSkip = useCallback(() => {
    setShowConfirmation(false);
    setWizard1Completed();
  }, [setWizard1Completed]);

  // Track current step target for custom spotlight
  const [currentTarget, setCurrentTarget] = useState<DOMRect | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Update spotlight position when step changes
  useEffect(() => {
    if (!showMenuTour || !tourState.run) {
      setCurrentTarget(null);
      return;
    }

    const updateSpotlight = () => {
      const step = MENU_TOUR_STEPS[currentStepIndex];
      if (step) {
        const element = document.querySelector(step.target as string);
        if (element) {
          setCurrentTarget(element.getBoundingClientRect());
        }
      }
    };

    // Initial update
    updateSpotlight();

    // Update on resize
    window.addEventListener('resize', updateSpotlight);
    return () => window.removeEventListener('resize', updateSpotlight);
  }, [showMenuTour, tourState.run, currentStepIndex]);

  // Collapse all menus when tour starts
  useEffect(() => {
    if (showMenuTour) {
      // Dispatch custom event to collapse all menus
      window.dispatchEvent(new CustomEvent('collapseAllMenus'));
    }
  }, [showMenuTour]);

  // Extended callback to track step changes
  const extendedCallback = useCallback((data: CallBackProps) => {
    const { index, type } = data;

    // Track step index for spotlight
    if (type === 'step:after' || type === 'step:before') {
      setCurrentStepIndex(index);
    }

    // Call original callback
    handleTourCallback(data);
  }, [handleTourCallback]);

  // Return null only if neither tour nor confirmation is showing
  if (!showMenuTour && !showConfirmation) {
    return null;
  }

  return (
    <>
      {/* Custom spotlight overlay that renders above sidebar and header */}
      {showMenuTour && tourState.run && currentTarget && (
        <>
          {/* Color overlay with spotlight cutout - starts below TopBar, stops before right sidebar to avoid double overlay */}
          {(() => {
            const topBarHeight = 72; // TOP_BAR_HEIGHT numeric value
            const spotTop = Math.max(0, currentTarget.top - 6 - topBarHeight);
            const spotBottom = Math.max(0, currentTarget.bottom + 6 - topBarHeight);
            return (
              <Box
                position="fixed"
                top={TOP_BAR_HEIGHT}
                left={0}
                right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
                bottom={0}
                zIndex={10001}
                pointerEvents="none"
                bg="blackAlpha.600"
                style={{
                  clipPath: `polygon(
                    0% 0%,
                    0% 100%,
                    ${currentTarget.left - 6}px 100%,
                    ${currentTarget.left - 6}px ${spotTop}px,
                    ${currentTarget.right + 6}px ${spotTop}px,
                    ${currentTarget.right + 6}px ${spotBottom}px,
                    ${currentTarget.left - 6}px ${spotBottom}px,
                    ${currentTarget.left - 6}px 100%,
                    100% 100%,
                    100% 0%
                  )`,
                }}
              />
            );
          })()}

          {/* Spotlight border */}
          <Box
            position="fixed"
            left={`${currentTarget.left - 6}px`}
            top={`${currentTarget.top - 6}px`}
            width={`${currentTarget.width + 12}px`}
            height={`${currentTarget.height + 12}px`}
            border="2px solid"
            borderColor="orange.400"
            borderRadius="8px"
            boxShadow="0 0 0 4px rgba(246, 133, 30, 0.3)"
            zIndex={10001}
            pointerEvents="none"
          />

          {/* Dark overlay for TopBar - same style as rest of overlay */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
            h={TOP_BAR_HEIGHT}
            zIndex={10202}
            bg="blackAlpha.600"
            backdropFilter="blur(4px)"
            pointerEvents="none"
          />

          {/* Dark overlay for Right Sidebar with blur - covers entire sidebar height */}
          <Box
            position="fixed"
            top={0}
            right={0}
            bottom={0}
            w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
            zIndex={10202}
            bg="blackAlpha.600"
            backdropFilter="blur(4px)"
            pointerEvents="none"
          />
        </>
      )}

      {showMenuTour && (
        <ProductTour
          state={tourState}
          callback={extendedCallback}
        />
      )}

      {/* Confirmation Modal - "Are you ready to create your first campaign?" */}
      {showConfirmation && (
        <>
          {/* Backdrop with blur effect - starts below TopBar, stops before right sidebar to avoid double overlay */}
          <Box
            position="fixed"
            top={TOP_BAR_HEIGHT}
            left={0}
            right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
            bottom={0}
            bg="blackAlpha.600"
            backdropFilter="blur(4px)"
            zIndex={10001}
            onClick={handleConfirmSkip}
          />

          {/* Dark overlay for TopBar - same style as rest of overlay */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
            h={TOP_BAR_HEIGHT}
            zIndex={10202}
            bg="blackAlpha.600"
            backdropFilter="blur(4px)"
            pointerEvents="none"
          />

          {/* Dark overlay for Right Sidebar - covers entire sidebar height */}
          <Box
            position="fixed"
            top={0}
            right={0}
            bottom={0}
            w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
            zIndex={10202}
            bg="blackAlpha.600"
            backdropFilter="blur(4px)"
            pointerEvents="none"
          />

          {/* Modal */}
          <Box
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            bg="white"
            borderRadius="16px"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
            maxW="420px"
            w="90%"
            zIndex={10002}
            overflow="hidden"
            sx={{
              animation: 'modalSlideIn 0.3s ease-out',
              '@keyframes modalSlideIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translate(-50%, -48%) scale(0.96)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translate(-50%, -50%) scale(1)',
                },
              },
            }}
          >
            {/* Close button - absolute positioned top-right on orange header */}
            <IconButton
              aria-label="Close"
              icon={<CloseIcon boxSize={3} />}
              position="absolute"
              top={3}
              right={3}
              size="sm"
              variant="ghost"
              color="white"
              onClick={handleConfirmSkip}
              _hover={{ bg: 'rgba(255,255,255,0.2)' }}
              zIndex={2}
            />

            {/* Header with gradient and icon */}
            <Box
              bg="linear-gradient(135deg, #F6851E 0%, #FF6B35 100%)"
              py={8}
              px={6}
              textAlign="center"
              position="relative"
              overflow="hidden"
            >
              {/* Decorative circles */}
              <Box
                position="absolute"
                top="-20px"
                right="-20px"
                w="80px"
                h="80px"
                borderRadius="full"
                bg="rgba(255,255,255,0.1)"
              />
              <Box
                position="absolute"
                bottom="-30px"
                left="-30px"
                w="100px"
                h="100px"
                borderRadius="full"
                bg="rgba(255,255,255,0.08)"
              />

              {/* Icon with pulse effect */}
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="64px"
                h="64px"
                borderRadius="full"
                bg="rgba(255,255,255,0.2)"
                mb={4}
                sx={{
                  animation: 'iconPulse 2s ease-in-out infinite',
                  '@keyframes iconPulse': {
                    '0%, 100%': {
                      transform: 'scale(1)',
                      boxShadow: '0 0 0 0 rgba(255,255,255,0.4)',
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 0 0 10px rgba(255,255,255,0)',
                    },
                  },
                }}
              >
                <Icon as={FaRocket} boxSize={7} color="white" />
              </Box>

              <Text fontSize="xl" fontWeight="bold" color="white">
                Ready to Create?
              </Text>
            </Box>

            {/* Content */}
            <VStack spacing={4} py={6} px={6} align="center" textAlign="center">
              <Text fontSize="md" color="gray.600" lineHeight="1.7">
                Let's create your first ad campaign together! We'll guide you through each step to make it easy.
              </Text>
            </VStack>

            {/* Footer with buttons */}
            <VStack spacing={3} px={6} pb={6}>
              <Button
                colorScheme="orange"
                size="lg"
                w="full"
                fontWeight="600"
                onClick={handleConfirmYes}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg',
                }}
                transition="all 0.2s"
              >
                Yes, let's go!
              </Button>
              <Button
                variant="ghost"
                size="md"
                color="gray.500"
                fontWeight="500"
                onClick={handleConfirmSkip}
                _hover={{ color: 'gray.700', bg: 'transparent' }}
              >
                Skip for now
              </Button>
            </VStack>
          </Box>
        </>
      )}
    </>
  );
};

export default MenuTour;
